/**
 * InventoryTab.js — On-hand levels, receive delivery, record donations, manual adjust
 * Extracted from AdminDashboard.js during Phase 1 refactor
 * RD-70: Added category grouping, search, and expand/collapse (parity with CatalogTab)
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import S from "./adminStyles";
import { INVENTORY_API } from "./adminConstants";
import { exportToCSV, printTable } from "../../utils/exportUtils";

export default function InventoryTab({ user }) {
  const [items, setItems] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeAction, setActiveAction] = useState(null); // "receive" | "donate" | "adjust" | "breakdown" | "transfer_to_client" | "transfer_to_backstock"
  const [actionItems, setActionItems] = useState([{ itemId: "", qty: 0 }]);
  const [actionMeta, setActionMeta] = useState({
    krogerOrderRef: "",
    donor: "",
    reason: "",
    notes: "",
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [actionResult, setActionResult] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [adjustErrors, setAdjustErrors] = useState({});
  const [suppliers, setSuppliers] = useState([]);
  const [receiveSupplierId, setReceiveSupplierId] = useState("kroger");
  const [receiveExpDate, setReceiveExpDate] = useState("");

  // ── D-6: Breakdown & Transfer state ───────────────────────────────
  const [backstockItems, setBackstockItems] = useState([]);
  const [btBackstockId, setBtBackstockId]   = useState("");
  const [btClientId, setBtClientId]         = useState("");
  const [btBulkUnits, setBtBulkUnits]       = useState(1);
  const [btClientUnits, setBtClientUnits]   = useState(1);
  const [btUnits, setBtUnits]               = useState(1);
  const [btConfirm, setBtConfirm]           = useState(false);
  const [btLoading, setBtLoading]           = useState(false);
  const [btError, setBtError]               = useState(null);

  // ── RD-70: Search + expand/collapse state ──────────────────────────
  const [searchQuery, setSearchQuery] = useState("");
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());

  // ── RD-71: Transaction log state ───────────────────────────────────
  const [showLog, setShowLog] = useState(false);
  const [logItems, setLogItems] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logError, setLogError] = useState(null);
  const [logHasMore, setLogHasMore] = useState(false);
  const [logCursor, setLogCursor] = useState(null);
  const [logFilterItem, setLogFilterItem] = useState("");
  const [logFilterType, setLogFilterType] = useState("");
  const [reorderDecisionsMap, setReorderDecisionsMap] = useState({}); // keyed by clientItemId
  const [decisionUpdating, setDecisionUpdating] = useState(new Set());

  const loadReorderDecisions = useCallback(async () => {
    try {
      const token = await user.getIdToken();
      const [nrRes, ordRes] = await Promise.all([
        fetch(`/api/reorder-decisions?status=needs_review&limit=200`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`/api/reorder-decisions?status=ordered&limit=200`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      const map = {};
      if (nrRes.ok) {
        const data = await nrRes.json();
        (data.decisions || []).forEach((d) => { map[d.clientItemId] = d; });
      }
      if (ordRes.ok) {
        const data = await ordRes.json();
        (data.decisions || []).forEach((d) => { map[d.clientItemId] = d; });
      }
      setReorderDecisionsMap(map);
    } catch (err) {
      console.error("loadReorderDecisions error:", err);
    }
  }, [user]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const token = await user.getIdToken();
      const [invRes, alertRes, suppRes, bsRes] = await Promise.all([
        fetch(INVENTORY_API, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${INVENTORY_API}/alerts`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`/api/suppliers`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`/api/backstock-items`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      if (invRes.ok) {
        const data = await invRes.json();
        setItems(data.items || []);
      }
      if (alertRes.ok) {
        const data = await alertRes.json();
        setAlerts(data.alerts || []);
      }
      if (suppRes.ok) {
        const data = await suppRes.json();
        setSuppliers(data.suppliers || data || []);
      }
      if (bsRes.ok) {
        const data = await bsRes.json();
        setBackstockItems(data.backstockItems || []);
      }
      loadReorderDecisions();
    } catch (err) {
      console.error("Inventory load error:", err);
    } finally {
      setLoading(false);
    }
  }, [user, loadReorderDecisions]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadReorderDecisions();
  }, [loadReorderDecisions]);

  const updateDecisionStatus = async (decisionId, newStatus) => {
    setDecisionUpdating((prev) => new Set([...prev, decisionId]));
    try {
      const token = await user.getIdToken();
      const res = await fetch(`/api/reorder-decisions/${decisionId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        await loadReorderDecisions();
      } else {
        console.error("Failed to update reorder decision:", await res.text());
      }
    } catch (err) {
      console.error("updateDecisionStatus error:", err);
    } finally {
      setDecisionUpdating((prev) => {
        const next = new Set(prev);
        next.delete(decisionId);
        return next;
      });
    }
  };

  const loadLog = useCallback(async (reset = true, cursor = null) => {
    setLogLoading(true);
    if (reset) setLogError(null);
    try {
      const token = await user.getIdToken();
      const params = new URLSearchParams({ limit: "50" });
      if (logFilterItem) params.set("itemId", logFilterItem);
      if (logFilterType) params.set("txType", logFilterType);
      if (!reset && cursor) params.set("startAfter", cursor);
      const res = await fetch(`${INVENTORY_API}/log?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("fetch failed");
      const data = await res.json();
      setLogItems((prev) => (reset ? data.logs : [...prev, ...data.logs]));
      setLogHasMore(data.hasMore);
      setLogCursor(data.nextCursor);
    } catch {
      setLogError("Could not load transaction history.");
    } finally {
      setLogLoading(false);
    }
  }, [user, logFilterItem, logFilterType]);

  useEffect(() => {
    if (!showLog) return;
    loadLog(true);
  }, [showLog, loadLog]);

  const addActionRow = () =>
    setActionItems((p) => [...p, { itemId: "", qty: 0 }]);
  const updateActionRow = (idx, field, value) => {
    setActionItems((p) =>
      p.map((r, i) => (i === idx ? { ...r, [field]: value } : r)),
    );
    if (field === "qty") {
      setAdjustErrors((p) => { const n = { ...p }; delete n[idx]; return n; });
    }
  };
  const removeActionRow = (idx) =>
    setActionItems((p) => p.filter((_, i) => i !== idx));

  const submitAction = async () => {
    const validItems = actionItems.filter((i) =>
      i.itemId && (activeAction === "adjust" ? parseInt(i.qty) !== 0 && !isNaN(parseInt(i.qty)) : i.qty > 0)
    );
    if (validItems.length === 0) return;
    if (activeAction === "adjust" && !actionMeta.reason.trim()) {
      alert("Please enter a reason for the adjustment.");
      return;
    }
    if (activeAction === "adjust") {
      const errors = {};
      for (const row of actionItems.filter(r => r.itemId && parseInt(r.qty) !== 0 && !isNaN(parseInt(r.qty)))) {
        const inventoryItem = items.find(i => i.itemId === row.itemId);
        if (inventoryItem && parseInt(row.qty) < 0 && Math.abs(parseInt(row.qty)) > (inventoryItem.onHandQty || 0)) {
          errors[actionItems.indexOf(row)] =
            `Cannot remove ${Math.abs(row.qty)} — only ${inventoryItem.onHandQty || 0} on hand`;
        }
      }
      if (Object.keys(errors).length > 0) {
        setAdjustErrors(errors);
        setActionLoading(false);
        return;
      }
    }

    setActionLoading(true);
    setActionResult(null);
    setActionError(null);

    try {
      const token = await user.getIdToken();
      let endpoint, body;

      if (activeAction === "receive") {
        endpoint = `${INVENTORY_API}/receive`;
        body = {
          items: validItems.map((i) => ({
            itemId: i.itemId,
            casesReceived: i.qty,
            supplierId: receiveSupplierId,
            expirationDate: receiveExpDate || null,
          })),
          source: receiveSupplierId,
          krogerOrderRef: actionMeta.krogerOrderRef,
          notes: actionMeta.notes,
          cycleId: null,
        };
      } else if (activeAction === "donate") {
        endpoint = `${INVENTORY_API}/donate`;
        body = {
          items: validItems,
          donor: actionMeta.donor,
          notes: actionMeta.notes,
        };
      } else if (activeAction === "adjust") {
        for (const item of validItems) {
          await fetch(`${INVENTORY_API}/adjust`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              itemId: item.itemId,
              qty: parseInt(item.qty),
              reason: actionMeta.reason,
            }),
          });
        }
        setActionResult({
          message: `${validItems.length} adjustment(s) recorded.`,
        });
        setActiveAction(null);
        setActionItems([{ itemId: "", qty: 0 }]);
        setAdjustErrors({});
        loadData();
        setActionLoading(false);
        return;
      }

      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        const data = await res.json();
        setActionResult(data);
        setActiveAction(null);
        setActionItems([{ itemId: "", qty: 0 }]);
        setAdjustErrors({});
        loadData();
      } else {
        const errData = await res.json().catch(() => ({}));
        setActionError(errData.message || errData.error || "Request failed. Please try again.");
      }
    } catch {
      setActionError("Network error. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  // D-6: reset breakdown/transfer form fields
  const resetBt = useCallback(() => {
    setBtBackstockId("");
    setBtClientId("");
    setBtBulkUnits(1);
    setBtClientUnits(1);
    setBtUnits(1);
    setBtConfirm(false);
    setBtError(null);
  }, []);

  const handleCancelAction = useCallback(() => {
    setActiveAction(null);
    resetBt();
  }, [resetBt]);

  useEffect(() => {
    if (!activeAction) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") handleCancelAction();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [activeAction, handleCancelAction]);

  // ── D-6: Breakdown & Transfer submit ──────────────────────────────
  // API paths are relative — same pattern as INVENTORY_API fetch calls above.
  // Auth token from user.getIdToken(), sent as Authorization: Bearer ${token}.
  const submitBtAction = async () => {
    setBtLoading(true);
    setBtError(null);
    try {
      const token = await user.getIdToken();
      let endpoint, body;
      if (activeAction === "breakdown") {
        endpoint = "/api/supply-transactions/breakdown";
        body = {
          backstockItemId:   btBackstockId,
          bulkUnitsConsumed: btBulkUnits,
          clientItemId:      btClientId,
          clientUnitsAdded:  btClientUnits,
        };
      } else if (activeAction === "transfer_to_client") {
        endpoint = "/api/supply-transactions/transfer-to-client";
        body = { backstockItemId: btBackstockId, clientItemId: btClientId, units: btUnits };
      } else {
        endpoint = "/api/supply-transactions/transfer-to-backstock";
        body = { clientItemId: btClientId, backstockItemId: btBackstockId, units: btUnits };
      }
      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const label = {
          breakdown:              "Breakdown",
          transfer_to_client:     "Transfer to shelf",
          transfer_to_backstock:  "Return to storage",
        }[activeAction];
        setActionResult({ message: `${label} recorded successfully.` });
        setActiveAction(null);
        resetBt();
        loadData();
      } else {
        const errData = await res.json().catch(() => ({}));
        setBtError(errData.error || "Request failed. Please try again.");
        setBtConfirm(false);
      }
    } catch {
      setBtError("Network error. Please try again.");
      setBtConfirm(false);
    } finally {
      setBtLoading(false);
    }
  };

  // ── RD-70: Category grouping + search derived state ────────────────
  const isSearching = searchQuery.trim().length > 0;
  const q = searchQuery.trim().toLowerCase();

  const sortedCategories = useMemo(
    () => [...new Set(items.map((i) => i.category || "Other"))].sort(),
    [items],
  );

  const itemsByCategory = useMemo(
    () =>
      Object.fromEntries(
        sortedCategories.map((cat) => [
          cat,
          items
            .filter((i) => (i.category || "Other") === cat)
            .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
        ]),
      ),
    [items, sortedCategories],
  );

  const filteredItems = useMemo(
    () =>
      isSearching
        ? items
            .filter(
              (i) =>
                (i.name || "").toLowerCase().includes(q) ||
                (i.itemId || "").toLowerCase().includes(q) ||
                (i.category || "").toLowerCase().includes(q),
            )
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
        : [],
    [items, isSearching, q],
  );

  const toggleCategory = (cat) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const expandAll = () => setCollapsedCategories(new Set());
  const collapseAll = () => setCollapsedCategories(new Set(sortedCategories));

  // ── Export / print ─────────────────────────────────────────────────
  const INVENTORY_COLS = [
    { key: "itemId", label: "Item ID" },
    { key: "name", label: "Name" },
    { key: "category", label: "Category" },
    { key: "onHandQty", label: "On Hand" },
    { key: "onHandVendor", label: "On Hand (Vendor)" },
    { key: "onHandDonated", label: "On Hand (Donated)" },
    { key: "reorderThreshold", label: "Reorder Threshold" },
    { key: "inStock", label: "In Stock" },
  ];

  const exportInventoryCSV = () => {
    const today = new Date().toISOString().split("T")[0];
    exportToCSV(`raider-depot-inventory-${today}.csv`, items, INVENTORY_COLS);
  };

  const printInventory = () => {
    printTable("Inventory", `${items.length} items`, INVENTORY_COLS, items);
  };

  // ── Row renderer ───────────────────────────────────────────────────
  const renderItemRow = (item) => {
    const available = (item.onHandQty || 0) - (item.allocatedQty || 0);
    return (
      <tr key={item.itemId}>
        <td style={S.td}>
          <strong>{item.name}</strong>
          {item.source === "donated" && (
            <span style={{ ...S.badge("#8b5cf6"), marginLeft: "6px" }}>
              donated
            </span>
          )}
        </td>
        <td
          style={{
            ...S.td,
            ...S.stockLevel(item.onHandQty || 0, item.reorderThreshold),
          }}
        >
          {item.onHandQty || 0}
        </td>
        <td style={S.td}>{item.onHandVendor || 0}</td>
        <td style={S.td}>{item.onHandDonated || 0}</td>
        <td style={S.td}>{item.allocatedQty || 0}</td>
        <td style={{ ...S.td, fontWeight: "700" }}>
          {Math.max(0, available)}
        </td>
        <td style={S.td}>{item.reorderThreshold || "—"}</td>
      </tr>
    );
  };

  // ── Column widths shared between sticky header + data table ────────
  const colWidths = ["32%", "10%", "12%", "10%", "10%", "10%", "10%"];
  const colHeaders = ["Item", "On Hand", "Vendor", "Donated", "Allocated", "Available", "Reorder At"];

  const SharedColgroup = () => (
    <colgroup>
      {colWidths.map((w, i) => <col key={i} style={{ width: w }} />)}
    </colgroup>
  );

  return (
    <>
      {/* Alerts */}
      {alerts.length > 0 && (
        <div style={S.alertBanner}>
          <div style={{ marginBottom: "6px" }}>
            ⚠️{" "}
            <strong>
              {alerts.filter((a) => a.alertType === "low_stock").length} item
              {alerts.filter((a) => a.alertType === "low_stock").length !== 1 ? "s" : ""} below reorder threshold
            </strong>
          </div>
          {alerts
            .filter((a) => a.alertType === "low_stock")
            .map((alert) => {
              const decision = reorderDecisionsMap[alert.itemId];
              const isUpdating = decision && decisionUpdating.has(decision.id);
              return (
                <div
                  key={alert.itemId}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flexWrap: "wrap",
                    marginBottom: "4px",
                    fontSize: "13px",
                  }}
                >
                  <span>
                    {alert.name} ({alert.onHandQty}/{alert.reorderThreshold})
                  </span>
                  {decision?.status === "needs_review" && (
                    <>
                      <span
                        style={{
                          backgroundColor: "#fef3c7",
                          color: "#92400e",
                          fontSize: "11px",
                          fontWeight: "600",
                          padding: "1px 7px",
                          borderRadius: "10px",
                        }}
                      >
                        Needs Review
                      </span>
                      <button
                        style={{
                          fontSize: "11px",
                          padding: "2px 10px",
                          borderRadius: "6px",
                          border: "1px solid #93c5fd",
                          backgroundColor: "#eff6ff",
                          color: "#1d4ed8",
                          cursor: isUpdating ? "not-allowed" : "pointer",
                          opacity: isUpdating ? 0.6 : 1,
                        }}
                        disabled={isUpdating}
                        onClick={() => updateDecisionStatus(decision.id, "ordered")}
                      >
                        {isUpdating ? "Saving…" : "Mark Ordered"}
                      </button>
                    </>
                  )}
                  {decision?.status === "ordered" && (
                    <>
                      <span
                        style={{
                          backgroundColor: "#dbeafe",
                          color: "#1e40af",
                          fontSize: "11px",
                          fontWeight: "600",
                          padding: "1px 7px",
                          borderRadius: "10px",
                        }}
                      >
                        Ordered
                      </span>
                      <button
                        style={{
                          fontSize: "11px",
                          padding: "2px 10px",
                          borderRadius: "6px",
                          border: "1px solid #86efac",
                          backgroundColor: "#f0fdf4",
                          color: "#15803d",
                          cursor: isUpdating ? "not-allowed" : "pointer",
                          opacity: isUpdating ? 0.6 : 1,
                        }}
                        disabled={isUpdating}
                        onClick={() => updateDecisionStatus(decision.id, "received")}
                      >
                        {isUpdating ? "Saving…" : "Mark Received"}
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          {alerts.some((a) => a.alertType !== "low_stock") && (
            <div style={{ marginTop: "4px", fontSize: "12px", opacity: 0.85 }}>
              {alerts
                .filter((a) => a.alertType !== "low_stock")
                .map((a) => `${a.name} (${a.alertType === "expired" ? "expired" : "expiring soon"})`)
                .join(", ")}
            </div>
          )}
        </div>
      )}

      {/* Action result */}
      {actionResult && (
        <div
          style={{
            ...S.card,
            backgroundColor: "#f0fdf4",
            border: "1px solid #22c55e",
          }}
        >
          <p style={{ color: "#16a34a", margin: 0, fontSize: "13px" }}>
            ✅ {actionResult.message || "Action completed."}
          </p>
        </div>
      )}

      {/* Action form */}
      {activeAction && (
        <div
          style={{
            ...S.card,
            border: `2px solid ${{
            receive:              "#3b82f6",
            donate:               "#8b5cf6",
            adjust:               "#f59e0b",
            breakdown:            "#10b981",
            transfer_to_client:   "#06b6d4",
            transfer_to_backstock:"#64748b",
          }[activeAction] || "#3b82f6"}`,
          }}
        >
          <h3
            style={{ fontSize: "14px", color: "#1e293b", margin: "0 0 12px 0" }}
          >
            {activeAction === "receive" ? "📥 Receive Delivery"
              : activeAction === "donate" ? "🎁 Record Donation"
              : activeAction === "adjust" ? "✏️ Manual Adjustment"
              : activeAction === "breakdown" ? "Break Down Backstock"
              : activeAction === "transfer_to_client" ? "Move to Client Shelf"
              : "Return to Storage"}
          </h3>

          {activeAction === "receive" && (
            <div
              style={{
                marginBottom: "12px",
                display: "flex",
                gap: "12px",
                flexWrap: "wrap",
                alignItems: "flex-end",
              }}
            >
              <div>
                <label
                  style={{
                    fontSize: "11px",
                    color: "#64748b",
                    fontWeight: "600",
                    display: "block",
                    marginBottom: "4px",
                  }}
                >
                  Supplier
                </label>
                <select
                  style={{ ...S.select, width: "200px" }}
                  value={receiveSupplierId}
                  onChange={(e) => setReceiveSupplierId(e.target.value)}
                >
                  {suppliers.map((s) => (
                    <option key={s.supplierId} value={s.supplierId}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  style={{
                    fontSize: "11px",
                    color: "#64748b",
                    fontWeight: "600",
                    display: "block",
                    marginBottom: "4px",
                  }}
                >
                  Expiration Date (optional)
                </label>
                <input
                  type="date"
                  style={{ ...S.input, width: "180px" }}
                  value={receiveExpDate}
                  onChange={(e) => setReceiveExpDate(e.target.value)}
                />
              </div>
              <div>
                <label
                  style={{
                    fontSize: "11px",
                    color: "#64748b",
                    fontWeight: "600",
                    display: "block",
                    marginBottom: "4px",
                  }}
                >
                  Order Reference
                </label>
                <input
                  style={{ ...S.input, width: "200px" }}
                  value={actionMeta.krogerOrderRef}
                  onChange={(e) =>
                    setActionMeta((p) => ({
                      ...p,
                      krogerOrderRef: e.target.value,
                    }))
                  }
                  placeholder="Order # or PO ref"
                />
              </div>
            </div>
          )}
          {activeAction === "donate" && (
            <div style={{ marginBottom: "12px" }}>
              <label
                style={{
                  fontSize: "11px",
                  color: "#64748b",
                  fontWeight: "600",
                }}
              >
                Donor
              </label>
              <input
                style={{ ...S.input, width: "300px" }}
                value={actionMeta.donor}
                onChange={(e) =>
                  setActionMeta((p) => ({ ...p, donor: e.target.value }))
                }
                placeholder="e.g., Football team food drive"
              />
            </div>
          )}
          {activeAction === "adjust" && (
            <div style={{ marginBottom: "12px" }}>
              <label
                style={{
                  fontSize: "11px",
                  color: "#64748b",
                  fontWeight: "600",
                }}
              >
                Reason
              </label>
              <input
                style={{ ...S.input, width: "300px" }}
                value={actionMeta.reason}
                onChange={(e) =>
                  setActionMeta((p) => ({ ...p, reason: e.target.value }))
                }
                placeholder="e.g., Physical count correction"
              />
            </div>
          )}

          {/* ── Existing multi-row form (receive / donate / adjust) ── */}
          {["receive", "donate", "adjust"].includes(activeAction) && (<>
            {actionItems.map((row, idx) => (
              <div key={idx} style={{ ...S.flexRow, marginBottom: "8px" }}>
                <select
                  style={{ ...S.select, flex: 2 }}
                  value={row.itemId}
                  onChange={(e) => updateActionRow(idx, "itemId", e.target.value)}
                >
                  <option value="">Select item...</option>
                  {items.map((i) => (
                    <option key={i.itemId} value={i.itemId}>
                      {i.name}
                    </option>
                  ))}
                </select>
                <input
                  style={{ ...S.input, width: "80px", flex: 0 }}
                  type="number"
                  inputMode="decimal"
                  value={row.qty}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "" || v === "-") { updateActionRow(idx, "qty", v); return; }
                    const parsed = parseInt(v, 10);
                    updateActionRow(idx, "qty", isNaN(parsed) ? 0 : parsed);
                  }}
                  onBlur={() => {
                    const v = actionItems[idx]?.qty;
                    if (v === "" || v === "-") updateActionRow(idx, "qty", 0);
                  }}
                  placeholder="Qty"
                />
                {activeAction === "adjust" && adjustErrors[idx] && (
                  <span style={{ fontSize: "11px", color: "#dc2626", fontWeight: "600", whiteSpace: "nowrap" }}>
                    ⚠ {adjustErrors[idx]}
                  </span>
                )}
                {actionItems.length > 1 && (
                  <button
                    style={S.btnDanger}
                    onClick={() => removeActionRow(idx)}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            {actionError && (
              <p style={{ color: "#dc2626", fontSize: "13px", marginBottom: "12px" }}>{actionError}</p>
            )}
            <div style={{ ...S.flexRow, marginTop: "12px" }}>
              <button style={S.btnSecondary} onClick={addActionRow}>
                + Add Row
              </button>
              <button
                style={S.btnPrimary}
                onClick={submitAction}
                disabled={actionLoading}
              >
                {actionLoading ? "Saving..." : "Submit"}
              </button>
              <button style={S.btnSecondary} onClick={handleCancelAction}>
                Cancel
              </button>
            </div>
          </>)}

          {/* ── D-6: Breakdown panel ── */}
          {activeAction === "breakdown" && (() => {
            const labelSt = { fontSize: "11px", color: "#64748b", fontWeight: "600", display: "block", marginBottom: "6px" };
            const bsName  = backstockItems.find(b => b.backstockItemId === btBackstockId)?.name;
            const catName = items.find(i => i.itemId === btClientId)?.name;
            if (!btConfirm) return (
              <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                <div>
                  <label style={labelSt}>Backstock Item (source)</label>
                  <select
                    style={{ ...S.select, width: "100%", fontSize: 16 }}
                    value={btBackstockId}
                    onChange={(e) => setBtBackstockId(e.target.value)}
                  >
                    <option value="">Select backstock item...</option>
                    {backstockItems.map((b) => (
                      <option key={b.backstockItemId} value={b.backstockItemId}>{b.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelSt}>Bulk Units to Consume</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    min="1"
                    style={{ ...S.input, width: "120px", fontSize: 16 }}
                    value={btBulkUnits}
                    onChange={(e) => setBtBulkUnits(Math.max(1, parseInt(e.target.value) || 1))}
                  />
                </div>
                <div>
                  <label style={labelSt}>Client Item (destination)</label>
                  <select
                    style={{ ...S.select, width: "100%", fontSize: 16 }}
                    value={btClientId}
                    onChange={(e) => setBtClientId(e.target.value)}
                  >
                    <option value="">Select client item...</option>
                    {items.map((i) => (
                      <option key={i.itemId} value={i.itemId}>{i.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelSt}>Client Units Produced</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    min="1"
                    style={{ ...S.input, width: "120px", fontSize: 16 }}
                    value={btClientUnits}
                    onChange={(e) => setBtClientUnits(Math.max(1, parseInt(e.target.value) || 1))}
                  />
                </div>
                <div style={S.flexRow}>
                  <button
                    style={S.btnPrimary}
                    disabled={!btBackstockId || !btClientId}
                    onClick={() => setBtConfirm(true)}
                  >
                    Review
                  </button>
                  <button style={S.btnSecondary} onClick={handleCancelAction}>Cancel</button>
                </div>
              </div>
            );
            return (
              <div>
                <div style={{ backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "8px", padding: "14px", marginBottom: "14px" }}>
                  <p style={{ margin: "0 0 6px 0", fontSize: "13px", color: "#1e293b" }}>
                    <strong>Consume from backstock:</strong> {btBulkUnits}x {bsName}
                  </p>
                  <p style={{ margin: 0, fontSize: "13px", color: "#1e293b" }}>
                    <strong>Produces on shelf:</strong> {btClientUnits}x {catName}
                  </p>
                </div>
                {btError && (
                  <p style={{ color: "#dc2626", fontSize: "13px", marginBottom: "12px" }}>{btError}</p>
                )}
                <div style={S.flexRow}>
                  <button
                    style={{ ...S.btnPrimary, backgroundColor: "#10b981" }}
                    onClick={submitBtAction}
                    disabled={btLoading}
                  >
                    {btLoading ? "Saving..." : "Confirm & Submit"}
                  </button>
                  <button style={S.btnSecondary} onClick={() => setBtConfirm(false)}>Back to Edit</button>
                </div>
              </div>
            );
          })()}

          {/* ── D-6: Transfer to client (→ Shelf) panel ── */}
          {activeAction === "transfer_to_client" && (() => {
            const labelSt = { fontSize: "11px", color: "#64748b", fontWeight: "600", display: "block", marginBottom: "6px" };
            const bsName  = backstockItems.find(b => b.backstockItemId === btBackstockId)?.name;
            const catName = items.find(i => i.itemId === btClientId)?.name;
            if (!btConfirm) return (
              <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                <div>
                  <label style={labelSt}>Backstock Item (source)</label>
                  <select
                    style={{ ...S.select, width: "100%", fontSize: 16 }}
                    value={btBackstockId}
                    onChange={(e) => setBtBackstockId(e.target.value)}
                  >
                    <option value="">Select backstock item...</option>
                    {backstockItems.map((b) => (
                      <option key={b.backstockItemId} value={b.backstockItemId}>{b.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelSt}>Client Item (destination)</label>
                  <select
                    style={{ ...S.select, width: "100%", fontSize: 16 }}
                    value={btClientId}
                    onChange={(e) => setBtClientId(e.target.value)}
                  >
                    <option value="">Select client item...</option>
                    {items.map((i) => (
                      <option key={i.itemId} value={i.itemId}>{i.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelSt}>Units to Move</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    min="1"
                    style={{ ...S.input, width: "120px", fontSize: 16 }}
                    value={btUnits}
                    onChange={(e) => setBtUnits(Math.max(1, parseInt(e.target.value) || 1))}
                  />
                </div>
                <div style={S.flexRow}>
                  <button
                    style={S.btnPrimary}
                    disabled={!btBackstockId || !btClientId}
                    onClick={() => setBtConfirm(true)}
                  >
                    Review
                  </button>
                  <button style={S.btnSecondary} onClick={handleCancelAction}>Cancel</button>
                </div>
              </div>
            );
            return (
              <div>
                <div style={{ backgroundColor: "#ecfeff", border: "1px solid #a5f3fc", borderRadius: "8px", padding: "14px", marginBottom: "14px" }}>
                  <p style={{ margin: "0 0 6px 0", fontSize: "13px", color: "#1e293b" }}>
                    <strong>From backstock:</strong> {btUnits}x {bsName}
                  </p>
                  <p style={{ margin: 0, fontSize: "13px", color: "#1e293b" }}>
                    <strong>To client shelf:</strong> {catName}
                  </p>
                </div>
                {btError && (
                  <p style={{ color: "#dc2626", fontSize: "13px", marginBottom: "12px" }}>{btError}</p>
                )}
                <div style={S.flexRow}>
                  <button
                    style={{ ...S.btnPrimary, backgroundColor: "#06b6d4" }}
                    onClick={submitBtAction}
                    disabled={btLoading}
                  >
                    {btLoading ? "Saving..." : "Confirm & Submit"}
                  </button>
                  <button style={S.btnSecondary} onClick={() => setBtConfirm(false)}>Back to Edit</button>
                </div>
              </div>
            );
          })()}

          {/* ── D-6: Transfer to backstock (→ Storage) panel ── */}
          {activeAction === "transfer_to_backstock" && (() => {
            const labelSt = { fontSize: "11px", color: "#64748b", fontWeight: "600", display: "block", marginBottom: "6px" };
            const bsName  = backstockItems.find(b => b.backstockItemId === btBackstockId)?.name;
            const catName = items.find(i => i.itemId === btClientId)?.name;
            if (!btConfirm) return (
              <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                <div>
                  <label style={labelSt}>Client Item (source)</label>
                  <select
                    style={{ ...S.select, width: "100%", fontSize: 16 }}
                    value={btClientId}
                    onChange={(e) => setBtClientId(e.target.value)}
                  >
                    <option value="">Select client item...</option>
                    {items.map((i) => (
                      <option key={i.itemId} value={i.itemId}>{i.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelSt}>Backstock Item (destination)</label>
                  <select
                    style={{ ...S.select, width: "100%", fontSize: 16 }}
                    value={btBackstockId}
                    onChange={(e) => setBtBackstockId(e.target.value)}
                  >
                    <option value="">Select backstock item...</option>
                    {backstockItems.map((b) => (
                      <option key={b.backstockItemId} value={b.backstockItemId}>{b.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelSt}>Units to Return</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    min="1"
                    style={{ ...S.input, width: "120px", fontSize: 16 }}
                    value={btUnits}
                    onChange={(e) => setBtUnits(Math.max(1, parseInt(e.target.value) || 1))}
                  />
                </div>
                <div style={S.flexRow}>
                  <button
                    style={S.btnPrimary}
                    disabled={!btClientId || !btBackstockId}
                    onClick={() => setBtConfirm(true)}
                  >
                    Review
                  </button>
                  <button style={S.btnSecondary} onClick={handleCancelAction}>Cancel</button>
                </div>
              </div>
            );
            return (
              <div>
                <div style={{ backgroundColor: "#f8fafc", border: "1px solid #cbd5e1", borderRadius: "8px", padding: "14px", marginBottom: "14px" }}>
                  <p style={{ margin: "0 0 6px 0", fontSize: "13px", color: "#1e293b" }}>
                    <strong>From client shelf:</strong> {btUnits}x {catName}
                  </p>
                  <p style={{ margin: 0, fontSize: "13px", color: "#1e293b" }}>
                    <strong>Return to backstock:</strong> {bsName}
                  </p>
                </div>
                {btError && (
                  <p style={{ color: "#dc2626", fontSize: "13px", marginBottom: "12px" }}>{btError}</p>
                )}
                <div style={S.flexRow}>
                  <button
                    style={{ ...S.btnPrimary, backgroundColor: "#475569" }}
                    onClick={submitBtAction}
                    disabled={btLoading}
                  >
                    {btLoading ? "Saving..." : "Confirm & Submit"}
                  </button>
                  <button style={S.btnSecondary} onClick={() => setBtConfirm(false)}>Back to Edit</button>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Sticky wrapper: toolbar row + column headers ── */}
      <div style={{ position: "sticky", top: 0, zIndex: 10, backgroundColor: "#ffffff", boxShadow: "0 2px 4px rgba(0,0,0,0.08)" }}>
        {/* Row 1: action buttons + search + expand/collapse */}
        <div style={{ padding: "12px 20px 8px" }}>
          <div style={{
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            padding: "10px 16px",
            backgroundColor: "#f8fafc",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "8px",
          }}>
            {/* Left: action buttons */}
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
              <button
                style={activeAction === "receive" ? S.btnPrimary : S.btnSecondary}
                onClick={() =>
                  setActiveAction(activeAction === "receive" ? null : "receive")
                }
              >
                📥 Receive Delivery
              </button>
              <button
                style={
                  activeAction === "donate"
                    ? { ...S.btnPrimary, backgroundColor: "#8b5cf6" }
                    : S.btnSecondary
                }
                onClick={() =>
                  setActiveAction(activeAction === "donate" ? null : "donate")
                }
              >
                🎁 Record Donation
              </button>
              <button
                style={
                  activeAction === "adjust"
                    ? { ...S.btnPrimary, backgroundColor: "#f59e0b" }
                    : S.btnSecondary
                }
                onClick={() =>
                  setActiveAction(activeAction === "adjust" ? null : "adjust")
                }
              >
                ✏️ Manual Adjustment
              </button>

              {/* D-6: separator + breakdown/transfer buttons */}
              <span style={{ width: "1px", height: "24px", backgroundColor: "#e2e8f0", display: "inline-block", alignSelf: "center" }} />
              <button
                style={
                  activeAction === "breakdown"
                    ? { ...S.btnPrimary, backgroundColor: "#10b981" }
                    : S.btnSecondary
                }
                onClick={() =>
                  setActiveAction(activeAction === "breakdown" ? null : "breakdown")
                }
              >
                Break Down
              </button>
              <button
                style={
                  activeAction === "transfer_to_client"
                    ? { ...S.btnPrimary, backgroundColor: "#06b6d4" }
                    : S.btnSecondary
                }
                onClick={() =>
                  setActiveAction(activeAction === "transfer_to_client" ? null : "transfer_to_client")
                }
              >
                &#x2192; Shelf
              </button>
              <button
                style={
                  activeAction === "transfer_to_backstock"
                    ? { ...S.btnPrimary, backgroundColor: "#475569" }
                    : S.btnSecondary
                }
                onClick={() =>
                  setActiveAction(activeAction === "transfer_to_backstock" ? null : "transfer_to_backstock")
                }
              >
                &#x2192; Storage
              </button>

              <button style={S.btnSecondary} onClick={exportInventoryCSV} disabled={items.length === 0}>
                Export CSV
              </button>
              <button style={S.btnSecondary} onClick={printInventory} disabled={items.length === 0}>
                Print
              </button>
            </div>

            {/* Right: search + expand/collapse */}
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              {!isSearching && (
                <>
                  <button
                    style={{
                      background: "none", border: "none", padding: 0, fontSize: "12px",
                      ...(collapsedCategories.size === 0
                        ? { color: "#9ca3af", cursor: "default", pointerEvents: "none" }
                        : { color: "#2563EB", cursor: "pointer", fontWeight: 600 }),
                    }}
                    onClick={expandAll}
                  >
                    Expand All
                  </button>
                  <span style={{ color: "#cbd5e1", fontSize: "12px" }}>|</span>
                  <button
                    style={{
                      background: "none", border: "none", padding: 0, fontSize: "12px",
                      ...(collapsedCategories.size === sortedCategories.length
                        ? { color: "#9ca3af", cursor: "default", pointerEvents: "none" }
                        : { color: "#2563EB", cursor: "pointer", fontWeight: 600 }),
                    }}
                    onClick={collapseAll}
                  >
                    Collapse All
                  </button>
                  <span style={{ color: "#cbd5e1", fontSize: "12px" }}>|</span>
                </>
              )}
              <div style={{ position: "relative" }}>
                <input
                  style={{ ...S.input, margin: 0, width: "220px" }}
                  inputMode="text"
                  placeholder="Search items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    style={{
                      position: "absolute", right: "8px", top: "50%",
                      transform: "translateY(-50%)", border: "none",
                      background: "transparent", color: "#6b7280",
                      fontSize: "16px", cursor: "pointer", lineHeight: 1, padding: 0,
                    }}
                    onClick={() => setSearchQuery("")}
                  >
                    &#x2715;
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: sticky column headers */}
        {!loading && (
          <div style={{ padding: "0 20px" }}>
            <table style={{ ...S.table, tableLayout: "fixed" }}>
              <SharedColgroup />
              <thead>
                <tr>
                  {colHeaders.map((h) => (
                    <th key={h} style={{ ...S.th, textAlign: h === "Item" ? "left" : "center" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
            </table>
          </div>
        )}
      </div>

      {/* ── Data table ── */}
      <div style={{ backgroundColor: "#fff", padding: "0 20px 20px", marginBottom: "16px" }}>
        {loading ? (
          <p style={{ color: "#64748b", padding: "20px 0" }}>Loading...</p>
        ) : (
          <>
            <table style={{ ...S.table, tableLayout: "fixed" }}>
              <SharedColgroup />
              {/* Invisible header to maintain column alignment with sticky header above */}
              <thead style={{ visibility: "hidden", height: 0 }}>
                <tr>
                  {colHeaders.map((h) => (
                    <th key={h} style={{ ...S.th, padding: 0, height: 0 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isSearching ? (
                  // ── Search results: flat list ──
                  filteredItems.length > 0 ? (
                    filteredItems.map(renderItemRow)
                  ) : (
                    <tr>
                      <td
                        colSpan={7}
                        style={{ ...S.td, textAlign: "center", color: "#94a3b8", padding: "24px" }}
                      >
                        No items match your search.
                      </td>
                    </tr>
                  )
                ) : (
                  // ── Grouped by category ──
                  sortedCategories.map((cat) => {
                    const catItems = itemsByCategory[cat];
                    const collapsed = collapsedCategories.has(cat);
                    return (
                      <React.Fragment key={cat}>
                        {/* Category header row */}
                        <tr
                          onClick={() => toggleCategory(cat)}
                          style={{
                            cursor: "pointer",
                            backgroundColor: "#f1f5f9",
                            userSelect: "none",
                          }}
                        >
                          <td
                            colSpan={7}
                            style={{
                              padding: "10px 12px",
                              fontWeight: "600",
                              fontSize: "13px",
                              color: "#1e293b",
                              borderBottom: "1px solid #e2e8f0",
                              minHeight: "44px",  // iOS tap target
                            }}
                          >
                            {collapsed ? "▶" : "▼"}{" "}
                            {cat}{" "}
                            <span style={{ fontWeight: 400, color: "#64748b" }}>
                              — {catItems.length} item{catItems.length !== 1 ? "s" : ""}
                            </span>
                          </td>
                        </tr>
                        {/* Item rows */}
                        {!collapsed && catItems.map(renderItemRow)}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </>
        )}
      </div>

      {/* ── RD-71: Transaction History panel ── */}
      <div style={{ backgroundColor: "#fff", padding: "0 20px 24px" }}>
        <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px" }}>
          {/* Toggle header */}
          <div
            onClick={() => setShowLog((v) => !v)}
            style={{
              padding: "12px 16px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              cursor: "pointer",
              userSelect: "none",
              backgroundColor: "#f8fafc",
              borderRadius: showLog ? "8px 8px 0 0" : "8px",
              borderBottom: showLog ? "1px solid #e2e8f0" : "none",
            }}
          >
            <span style={{ fontWeight: "600", fontSize: "14px", color: "#1e293b" }}>
              Transaction History
            </span>
            <span style={{ fontSize: "11px", color: "#64748b" }}>
              {showLog ? "▼" : "▶"}
            </span>
          </div>

          {showLog && (
            <div style={{ padding: "16px" }}>
              {/* Filters */}
              <div style={{ display: "flex", gap: "12px", marginBottom: "12px", flexWrap: "wrap" }}>
                <select
                  style={{ ...S.select, width: "220px" }}
                  value={logFilterItem}
                  onChange={(e) => setLogFilterItem(e.target.value)}
                >
                  <option value="">All items</option>
                  {items.map((i) => (
                    <option key={i.itemId} value={i.itemId}>{i.name}</option>
                  ))}
                </select>
                <select
                  style={{ ...S.select, width: "160px" }}
                  value={logFilterType}
                  onChange={(e) => setLogFilterType(e.target.value)}
                >
                  <option value="">All types</option>
                  <option value="receive_vendor">Vendor receipt</option>
                  <option value="receive_donation">Donation</option>
                  <option value="adjustment">Adjustment</option>
                  <option value="shrinkage">Shrinkage</option>
                </select>
              </div>

              {logLoading && logItems.length === 0 ? (
                <p style={{ color: "#64748b", fontSize: "13px", margin: 0 }}>Loading...</p>
              ) : logError ? (
                <p style={{ color: "#dc2626", fontSize: "13px", margin: 0 }}>{logError}</p>
              ) : logItems.length === 0 ? (
                <p style={{ color: "#94a3b8", fontSize: "13px", margin: 0 }}>No transactions recorded yet.</p>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ ...S.table, minWidth: "680px" }}>
                    <thead>
                      <tr>
                        {["Date/Time", "Type", "Item", "Qty Change", "Qty After", "Source / Reason", "Recorded By"].map((h) => (
                          <th key={h} style={S.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {logItems.map((row) => {
                        const badgeColor = {
                          receive_vendor: "#3b82f6",
                          receive_donation: "#8b5cf6",
                          adjustment: "#f59e0b",
                          shrinkage: "#ef4444",
                        }[row.txType] || "#64748b";
                        const badgeLabel = {
                          receive_vendor: "Vendor",
                          receive_donation: "Donation",
                          adjustment: "Adjustment",
                          shrinkage: "Shrinkage",
                        }[row.txType] || row.txType;
                        const sourceText = row.supplierId || row.donorName || row.reason || "—";
                        const dateStr = row.createdAt
                          ? new Date(row.createdAt).toLocaleString("en-US", {
                              month: "short", day: "numeric",
                              hour: "numeric", minute: "2-digit", hour12: true,
                            })
                          : "—";
                        return (
                          <tr key={row.id} style={{ minHeight: "44px" }}>
                            <td style={{ ...S.td, whiteSpace: "nowrap", fontSize: "12px" }}>{dateStr}</td>
                            <td style={S.td}>
                              <span style={{ ...S.badge(badgeColor), fontSize: "11px" }}>{badgeLabel}</span>
                            </td>
                            <td style={{ ...S.td, fontSize: "13px" }}>{row.itemName || row.itemId}</td>
                            <td style={{ ...S.td, fontWeight: "700", color: row.qtyDelta > 0 ? "#16a34a" : "#dc2626" }}>
                              {row.qtyDelta > 0 ? `+${row.qtyDelta}` : row.qtyDelta}
                            </td>
                            <td style={S.td}>{row.qtyAfter != null ? row.qtyAfter : "—"}</td>
                            <td style={{ ...S.td, fontSize: "12px", color: "#475569" }}>{sourceText}</td>
                            <td style={{ ...S.td, fontSize: "12px", color: "#475569" }}>
                              {row.recordedByEmail || row.recordedBy || "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {logHasMore && (
                <div style={{ textAlign: "center", marginTop: "12px" }}>
                  <button
                    style={S.btnSecondary}
                    onClick={() => loadLog(false, logCursor)}
                    disabled={logLoading}
                  >
                    {logLoading ? "Loading..." : "Load More"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
